import { useEffect } from 'react'
import * as THREE from 'three'
import { screenToWorldPosition } from '@/utils/coordinates'
import { METERS_TO_FEET } from '@/constants/units'
import { useEventPlannerStore } from '../store/eventPlannerStore'
import { placementScenePosition } from '../motion/placementPose'
import type { MovementPlan } from '../motion/movementPlan'

interface UseEventAircraftPickingOptions {
  map: mapboxgl.Map | null
  mapCenter: { lat: number; lng: number } | undefined
  plans: Record<string, MovementPlan>
  /** When false, clicks are ignored (e.g. while a right-rail editor is open). */
  enabled: boolean
}

/**
 * Make the event-planner aircraft clickable in the 3D scene.
 *
 * MapboxScene's built-in raycast picking feeds the *hangar* editor store, so we
 * run our own lightweight ground-projection hit test instead (same approach as
 * `useObstacleDragInteraction`): project the cursor onto the ground plane, then
 * find the nearest *visible* placement whose footprint contains the point and
 * select it. An empty click clears the selection. Live position comes from the
 * shared `placementScenePosition` so a taxiing plane is still hit where it's drawn.
 */
export function useEventAircraftPicking({
  map,
  mapCenter,
  plans,
  enabled,
}: UseEventAircraftPickingOptions) {
  useEffect(() => {
    if (!map || !mapCenter || !enabled) return

    const handleClick = (e: mapboxgl.MapMouseEvent) => {
      const store = useEventPlannerStore.getState()
      const { placements, currentTime, catalog, motionConfig } = store

      const canvas = map.getCanvas()
      const rect = canvas.getBoundingClientRect()
      const ndc = new THREE.Vector2(
        ((e.point.x) / rect.width) * 2 - 1,
        -((e.point.y) / rect.height) * 2 + 1
      )
      const worldPos = screenToWorldPosition(ndc, map, [mapCenter.lng, mapCenter.lat])
      if (!worldPos) return

      const clickX = worldPos.x * METERS_TO_FEET
      const clickZ = worldPos.z * METERS_TO_FEET

      let bestId: string | null = null
      let bestDist = Infinity

      for (const p of Object.values(placements)) {
        // Same visibility predicate as useEventSceneSync.
        if (!(p.arrival <= currentTime && currentTime < p.departure)) continue

        const [sx, , sz] = placementScenePosition(p, plans, currentTime, motionConfig.enableAnimation)
        const dx = clickX - sx
        const dz = clickZ - sz
        const dist = Math.sqrt(dx * dx + dz * dz)

        // Hit radius from the catalog footprint (feet), with a small floor so
        // tiny pistons stay clickable.
        const entry = catalog.get(p.typeCode)
        const span = entry ? Math.max(entry.footprint.wingspan, entry.footprint.length) : 60
        const radius = Math.max(span / 2, 25)

        if (dist <= radius && dist < bestDist) {
          bestDist = dist
          bestId = p.eventAllocationId
        }
      }

      store.select(bestId)
    }

    map.on('click', handleClick)
    return () => {
      map.off('click', handleClick)
    }
  }, [map, mapCenter, plans, enabled])

  return null
}
